import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { api, useAuth } from '../App';
import { setWorkspaceSession } from '../lib/workspaceSession';

const Context = createContext(null);
export const useWorkspaces = () => useContext(Context);
export const workspaceError = e => e?.response?.data?.detail?.message || (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'No pudimos completar la operación.');

export function WorkspaceProvider({ children }) {
  const { user, appSettings } = useAuth();
  const portalHidden = appSettings?.hidden_modules?.includes('tableros') === true;
  const [data, setData] = useState(null);
  const [activeId, setActiveId] = useState('');
  const [error, setError] = useState('');
  const [disabled, setDisabled] = useState(false);
  const [epoch, setEpoch] = useState(0);
  const [changed, setChanged] = useState(false);
  const [lostAccess, setLostAccess] = useState(false);
  const revision = useRef(null);
  const conflictPending = useRef(false);
  const reload = useCallback(() => {
    conflictPending.current = false;
    setWorkspaceSession(activeId, { force: true });
    setChanged(false); setEpoch(value => value + 1);
  }, [activeId]);
  const key = `aorra.workspace.${user.id}`;
  const refresh = useCallback(async () => {
    try {
      const { data: result } = await api.get('/workspaces');
      setData(result); setDisabled(false); setError('');
      return result;
    } catch (e) {
      if (e.response?.status === 404) { setDisabled(true); setWorkspaceSession(''); return null; }
      setError(workspaceError(e)); throw e;
    }
  }, []);
  useEffect(() => {
    let cancelled = false;
    refresh().then(result => {
      if (!result || cancelled) return;
      const previous = sessionStorage.getItem(key);
      const chosen = result.workspaces.find(w => w.id === previous && w.available && !w.archived)?.id || result.workspaces.find(w => w.id === result.primary_id && w.available && !w.archived)?.id || result.workspaces.find(w => w.available && !w.archived)?.id || result.primary_id;
      setWorkspaceSession(chosen); setActiveId(chosen);
    }).catch(() => {});
    // Dependency changes can restart this effect while the current dashboard is
    // still mounted. Clearing the global scope here would invalidate valid
    // in-flight responses. Logout and access-revocation paths clear it explicitly.
    return () => { cancelled = true; };
  }, [key, refresh, portalHidden]);
  const select = useCallback((id) => {
    setWorkspaceSession(id); sessionStorage.setItem(key, id); setActiveId(id);
  }, [key]);
  useEffect(() => {
    if (!activeId || disabled) return;
    let stopped = false;
    revision.current = null;
    conflictPending.current = false;
    const poll = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const { data: result } = await api.get(`/workspaces/${activeId}/revision`);
        if (stopped) return;
        const signature = `${result.revision}:${result.membership_version}:${result.writable}`;
        if (revision.current !== null && revision.current !== signature) {
          await refresh();
          const editing = conflictPending.current || document.querySelector('[role="dialog"], form') || ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName);
          if (editing) setChanged(true); else reload();
        }
        revision.current = signature;
      } catch (e) {
        if (!stopped && e.response?.status === 503) setChanged(true);
        if (!stopped && [403, 404].includes(e.response?.status)) {
          setWorkspaceSession(''); setLostAccess(true);
          const result = await refresh().catch(() => null);
          if (result && !stopped) { select(result.primary_id); setLostAccess(false); }
        }
      }
    };
    const conflict = event => { if (event.detail === activeId) { conflictPending.current = true; setChanged(true); poll(); } };
    window.addEventListener('aorra:workspace-conflict', conflict);
    poll();
    const timer = setInterval(poll, 5000);
    return () => { stopped = true; clearInterval(timer); window.removeEventListener('aorra:workspace-conflict', conflict); };
  }, [activeId, disabled, refresh, select, reload]);
  if (disabled) return children;
  if (error && !data) return <div className="p-6"><p role="alert">{error}</p><button onClick={() => window.location.reload()}>Reintentar</button></div>;
  if (!data || !activeId || lostAccess) return <p className="p-6" role="status">Cargando tus tableros…</p>;
  const active = data.workspaces.find(w => w.id === activeId);
  return <Context.Provider value={{ ...data, actorId: user.id, active, select, refresh, error, changed, reload }}>
    <React.Fragment key={`${activeId}:${epoch}`}>{children}</React.Fragment>
  </Context.Provider>;
}
